/**
 * ContextMenu.tsx — a position-absolute right-click menu.
 *
 * Rendered by GraphCanvas in response to `onContextMenu` from the pane,
 * nodes, and edges. Generic over a list of `MenuEntry`: each entry has a
 * label, optional Phosphor icon, optional `danger` styling, and a callback.
 *
 * Closes on: any entry select, overlay click, or Escape. The overlay is a
 * full-screen transparent click-catcher that also captures the next context
 * event so the browser's native menu doesn't compete with ours.
 */
import { useEffect, type ReactNode } from 'react';

export interface MenuEntry {
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** When set, the item renders as a toggle with a checkmark on the right when
   *  `checked` is true. Used for boolean flags like "Background". */
  checked?: boolean;
  onSelect: () => void;
}

export interface ContextMenuState {
  x: number;
  y: number;
  entries: MenuEntry[];
}

interface ContextMenuProps {
  menu: ContextMenuState;
  onClose: () => void;
}

export function ContextMenu({ menu, onClose }: ContextMenuProps): JSX.Element {
  // Escape closes; clamp the menu inside the viewport after render.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Estimate size to keep the panel on screen. Cheap heuristic: assume the
  // panel won't exceed 280×(entries*36 + padding). Reflow on the next frame
  // would be cleaner but adds complexity for marginal gain at this size.
  const maxW = 280;
  const estH = menu.entries.length * 34 + 16;
  const x = Math.min(menu.x, window.innerWidth - maxW - 8);
  const y = Math.min(menu.y, window.innerHeight - estH - 8);

  return (
    <>
      {/* Overlay catches clicks + the native context event so the menu
          dismisses cleanly when the user clicks elsewhere or right-clicks. */}
      <div
        className="context-overlay"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        className="context-menu"
        role="menu"
        style={{ left: x, top: y }}
        onClick={(e) => e.stopPropagation()}
      >
        {menu.entries.map((entry, i) => (
          <button
            key={i}
            type="button"
            role="menuitem"
            className={`context-item${entry.danger ? ' danger' : ''}`}
            disabled={entry.disabled}
            onClick={() => {
              entry.onSelect();
              onClose();
            }}
          >
            {entry.icon && <span className="context-item-icon">{entry.icon}</span>}
            <span className="context-item-label">{entry.label}</span>
            {entry.checked === true && (
              <span className="context-item-check" aria-hidden="true">✓</span>
            )}
          </button>
        ))}
      </div>
    </>
  );
}
